import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
// @ts-ignore — JS module
import keycloak from '@/keycloak';
import { RoleNames } from '@/config/roles';
import { appConfig } from '@/config/appConfig';
import { useUserRoles } from '@/hooks/User/useUserRoles';
import useUser from '@/hooks/useUser';
import RoleSelection from './RoleSelection';
import './UserMenu.css';

interface UserDetails {
  username?: string;
  fullName?: string;
  name?: string;
  email?: string;
  personId?: number | string;
  cernPersonId?: number | string;
  authorities?: string[];
  allAuthorities?: string[];
  person?: {
    firstName?: string | null;
    lastName?: string | null;
    preferredEmail?: string | null;
    genericEmail?: string | null;
  } | null;
}

const compactName = (...parts: Array<string | null | undefined>) =>
  parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');

export const getUserDisplayName = (user?: UserDetails | null) =>
  compactName(user?.person?.firstName, user?.person?.lastName) ||
  user?.fullName ||
  user?.name ||
  user?.username ||
  'Unknown User';

export const getUserEmail = (user?: UserDetails | null) =>
  user?.email || user?.person?.preferredEmail || user?.person?.genericEmail || '';

export const getInitials = (names?: string) => {
  if (!names) return '?';
  const parts = names.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserMenu: React.FC = () => {
  const user = useUser();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    allRoles,
    activeRoles,
    hasActiveRole,
    toggleActiveRole,
    resetActiveRoles,
    clearActiveRoles,
    isAllRolesActive,
  } = useUserRoles(user);

  const displayName = getUserDisplayName(user);
  const userEmail = getUserEmail(user);
  const personId = user?.cernPersonId ?? user?.personId;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    clearActiveRoles();
    keycloak?.logout();
  };

  const getActiveRolesDisplay = (): string => {
    if (!appConfig.activeRolesFeature.active || isAllRolesActive()) {
      return 'All roles';
    }
    if (activeRoles.length === 0) {
      return 'No active roles';
    }
    return activeRoles.map(role => RoleNames[role] ?? role).join(', ');
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        className="avatar-button"
        aria-label="user menu"
        data-tutorial="user-menu"
      >
        <Avatar className="user-avatar">{getInitials(displayName)}</Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        className="user-menu"
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box className="user-menu-header">
          <Avatar className="user-menu-avatar">{getInitials(displayName)}</Avatar>
          <Box className="user-menu-info">
            <Typography variant="subtitle1" fontWeight="bold">
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {userEmail || 'No email'}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <MenuItem>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="caption" color="text.secondary">
              Username
            </Typography>
            <Typography variant="body2">{user?.username || 'N/A'}</Typography>
          </ListItemText>
        </MenuItem>

        {personId && (
          <MenuItem>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="caption" color="text.secondary">
                Person ID
              </Typography>
              <Typography variant="body2">{personId}</Typography>
            </ListItemText>
          </MenuItem>
        )}

        <Divider />

        <RoleSelection
          allRoles={allRoles}
          activeRoles={activeRoles}
          hasActiveRole={hasActiveRole}
          toggleActiveRole={toggleActiveRole}
          resetActiveRoles={resetActiveRoles}
          isAllRolesActive={isAllRolesActive}
          getActiveRolesDisplay={getActiveRolesDisplay}
        />

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
